import { useCallback, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, LinearProgress, Paper, Typography } from "@mui/material";
import {
  CheckCircle, CheckCircleOutline, EditOutlined, PictureAsPdfOutlined,
  PlayCircleOutline, RadioButtonUnchecked, ArticleOutlined,
} from "@mui/icons-material";
import apiService from "../../../service/Api";
import type { AcademyCourse, LessonType } from "./types";
import { BackButton, PatternPanel, PrimaryButton, StatusPill, learnerStatus, useAcademyColors } from "./AcademyUi";

const LESSON_ICONS: Record<LessonType, typeof PlayCircleOutline> = {
  video: PlayCircleOutline, text: ArticleOutlined, pdf: PictureAsPdfOutlined,
};

const embedUrl = (url?: string | null) => {
  if (!url) return "";
  const youtube = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^?&/]+)/);
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
};

export default function AcademyCourseViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState<AcademyCourse | null>(null);
  const [selectedId, setSelectedId] = useState<number | undefined>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const { colors, isDarkMode } = useAcademyColors();

  const load = useCallback(() => apiService.get<AcademyCourse>(`/api/lms/courses/${id}/`)
    .then((response) => {
      setCourse(response.data);
      const lessons = response.data.modules.flatMap((module) => module.lessons);
      setSelectedId((current) => current && lessons.some((lesson) => lesson.id === current) ? current : lessons[0]?.id);
    }).catch(() => setError("This course is unavailable or you do not have access.")), [id]);

  useEffect(() => { load(); }, [load]);

  // Opening a course records the enrollment (or refreshes last_accessed) for learner metrics.
  // Managers are skipped so previewing a course does not count as enrolling.
  const courseId = course?.id;
  const canManage = course?.can_manage;
  useEffect(() => {
    if (!courseId || canManage) return;
    apiService.post(`/api/lms/courses/${courseId}/enroll/`).catch(() => undefined);
  }, [courseId, canManage]);
  const lessons = useMemo(() => course?.modules.flatMap((module) => module.lessons) ?? [], [course]);
  const selected = lessons.find((lesson) => lesson.id === selectedId);

  const toggleComplete = async () => {
    if (!selected?.id) return;
    setSaving(true);
    try {
      await apiService.post(`/api/lms/courses/${id}/complete-lesson/`, {
        lesson_id: selected.id, completed: !selected.completed,
      });
      await load();
    } finally { setSaving(false); }
  };

  if (error) return (
    <Box>
      <BackButton onClick={() => navigate("/academy/courses")} />
      <Paper elevation={0} sx={{ backgroundColor: colors.primary[900], borderRadius: "12px", p: 4, textAlign: "center" }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    </Box>
  );
  if (!course) return <Box display="flex" justifyContent="center" py={8}><CircularProgress sx={{ color: "#4285F4" }} /></Box>;

  const lessonNumber = selected ? lessons.indexOf(selected) + 1 : 0;
  const nextLesson = selected ? lessons[lessons.indexOf(selected) + 1] : undefined;
  const cardSx = {
    borderRadius: 4, backgroundColor: isDarkMode ? colors.primary[900] : "#fff",
    border: `1px solid ${colors.primary[300]}15`,
  } as const;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <BackButton onClick={() => navigate(-1)} />
        {course.can_manage && (
          <Box sx={{ mb: 2 }}>
            <PrimaryButton startIcon={<EditOutlined />} onClick={() => navigate(`/academy/admin/courses/${course.id}/edit`)}>
              Edit Course
            </PrimaryButton>
          </Box>
        )}
      </Box>

      {/* Course summary */}
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2.5, md: 3 }, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: { xs: "wrap", md: "nowrap" } }}>
          {course.image && (
            <Box component="img" src={course.image} alt=""
              sx={{ width: { xs: "100%", md: 180 }, height: 110, objectFit: "cover", borderRadius: "10px", flexShrink: 0 }} />
          )}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 0.5 }}>
              {course.category && (
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: colors.primary[400], textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {course.category}
                </Typography>
              )}
              {course.can_manage ? <StatusPill status={course.status} /> : <StatusPill status={learnerStatus(course)} />}
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 22 }}>{course.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{course.description}</Typography>
            {course.skills.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Skills: {course.skills.map((skill) => skill.name).join(", ")}
              </Typography>
            )}
          </Box>
          <Box sx={{ width: { xs: "100%", md: 220 }, flexShrink: 0 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{course.module_count} modules · {course.lesson_count} lessons</Typography>
              <Typography variant="caption" fontWeight={700}>{course.progress}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={course.progress}
              sx={{ height: 8, borderRadius: 4, backgroundColor: `${colors.primary[400]}20`,
                "& .MuiLinearProgress-bar": { backgroundColor: "#614438", borderRadius: 4 } }} />
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.8fr) minmax(300px, 1fr)" }, gap: 3, alignItems: "start" }}>
        {/* Lesson content */}
        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2.5, md: 4 }, minHeight: 480 }}>
          {!selected ? (
            <Box textAlign="center" py={8}><Typography color="text.secondary">This course does not contain any lessons yet.</Typography></Box>
          ) : (
            <>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Lesson {lessonNumber} of {lessons.length}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 22, mb: 3 }}>{selected.title}</Typography>
              {selected.content_type === "video" && (
                <Box component="iframe" src={embedUrl(selected.video_url)} title={selected.title}
                  sx={{ width: "100%", aspectRatio: "16 / 9", border: 0, borderRadius: "12px" }} allowFullScreen />
              )}
              {selected.content_type === "text" && (
                <Box className="academy-lesson-content"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selected.content_text || "") }}
                  sx={{ lineHeight: 1.8, "& img": { maxWidth: "100%" } }} />
              )}
              {selected.content_type === "pdf" && selected.pdf_file && (
                <Box component="iframe" src={selected.pdf_file} title={selected.title}
                  sx={{ width: "100%", height: { xs: 500, md: 720 }, border: "1px solid", borderColor: "divider", borderRadius: "12px" }} />
              )}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mt: 4, pt: 2,
                borderTop: `1px solid ${isDarkMode ? colors.primary[700] : "#E2E8F0"}`, flexWrap: "wrap" }}>
                <Button
                  onClick={toggleComplete}
                  disabled={saving}
                  startIcon={selected.completed ? <CheckCircle /> : <CheckCircleOutline />}
                  sx={{
                    border: "1px solid", borderColor: selected.completed ? "#34A853" : isDarkMode ? `${colors.primary[300]}50` : "#CBD5E0",
                    color: selected.completed ? "#2E7D32" : isDarkMode ? colors.primary[200] : "#4A5568",
                    fontWeight: 600, textTransform: "none", borderRadius: "10px", px: 3, py: 1,
                  }}
                >
                  {saving ? "Saving..." : selected.completed ? "Completed — mark as incomplete" : "Mark as complete"}
                </Button>
                {nextLesson && (
                  <PrimaryButton startIcon={<PlayCircleOutline />} onClick={() => setSelectedId(nextLesson.id)}>Next lesson</PrimaryButton>
                )}
              </Box>
            </>
          )}
        </Paper>

        {/* Course outline */}
        <PatternPanel title="Course Content" badge={`${lessons.filter((lesson) => lesson.completed).length}/${lessons.length} done`}
          bodySx={{ p: 1.5 }}>
          {course.modules.map((module, moduleIndex) => (
            <Box key={module.id ?? moduleIndex} sx={{ mb: 1 }}>
              <Typography sx={{ px: 1.5, py: 1, fontSize: 13, fontWeight: 700, color: "text.secondary" }}>
                Module {moduleIndex + 1} · {module.title}
              </Typography>
              {module.lessons.map((lesson) => {
                const Icon = LESSON_ICONS[lesson.content_type];
                const active = lesson.id === selectedId;
                return (
                  <Box key={lesson.id} onClick={() => setSelectedId(lesson.id)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1.1, borderRadius: "10px", cursor: "pointer",
                      backgroundColor: active ? `${colors.primary[400]}18` : "transparent",
                      borderLeft: `3px solid ${active ? colors.primary[400] : "transparent"}`,
                      "&:hover": { backgroundColor: "rgba(114,78,65,0.05)" },
                    }}>
                    {lesson.completed
                      ? <CheckCircle sx={{ fontSize: 20, color: "#34A853" }} />
                      : <RadioButtonUnchecked sx={{ fontSize: 20, color: "text.disabled" }} />}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: active ? 700 : 500, color: active ? colors.primary[400] : "text.primary" }} noWrap>
                        {lesson.title}
                      </Typography>
                    </Box>
                    <Icon sx={{ fontSize: 18, color: "text.secondary" }} />
                  </Box>
                );
              })}
            </Box>
          ))}
        </PatternPanel>
      </Box>
    </motion.div>
  );
}
